# Generated from app.intent.md — do not edit manually
# Route: /materials/lots/:lotId
# Audience: Operators (md+), blocked on sm
# Ref: app.intent.md § Lot Detail

page-lot-detail:
  type: Page
  archetype: detail
  meta:
    title: $t.lot_detail.meta_title
    auth: required
  data:
    lot:
      model: ItemLot
      key: $route.lotId
      required: true
    # The genealogy card is a secondary part of the page: a failed genealogy read leaves the
    # lot on screen and the card empty, recorded under `_dataErrors.genealogy`.
    genealogy:
      model: Genealogy
      key: $route.lotId
      required: false
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - lot-detail-sm-notice
    - lot-detail-content

# ── sm viewport notice ───────────────────────────────────────────────────────

lot-detail-sm-notice:
  type: SmNotice
  content: $t.common.sm_notice_tablet

# ── Main content (hidden on sm) ──────────────────────────────────────────────

lot-detail-content:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    responsive:
      sm:
        display: none
  children:
    - lot-detail-header
    - lot-detail-info
    - lot-detail-genealogy

# ── Header: title + status badge + lifecycle actions ────────────────────────
#
# PageHeader.actions applies the default-variant rule (XP85-UX-001):
#   position 0 → filled   (Release — the primary affordance)
#   position 1+ → outlined (Inspect, Consume)
# Destructive actions (Scrap) and navigation (Audit trail) belong in the
# overflow menu so they can't be clicked by accident.

lot-detail-header:
  type: PageHeader
  back_route: /materials/lots
  title_prefix: $t.lot_detail.title_prefix
  bind:
    content: lot.lotNumber
  actions:
    - label: $t.lot_detail.btn_release
      a11y:
        label: $t.lot_detail.btn_release_label
      action:
        type: custom
        event: release-lot
      color: go
    - label: $t.lot_detail.btn_inspect
      a11y:
        label: $t.lot_detail.btn_inspect_label
      action:
        type: navigate
        route: /materials/lots/:lotId/inspect
        params:
          lotId: $route.lotId
      color: info
    - label: $t.lot_detail.btn_consume
      a11y:
        label: $t.lot_detail.btn_consume_label
      action:
        type: custom
        event: consume-lot
      color: caution
  overflow:
    # This was the sole remaining consumer of the dead `/audit/:entityId`
    # path-param route (audit-trail.cir.yaml never read $route.entityId into
    # its filters, so this link silently dropped the filter; the route
    # registration itself has been removed from app.cir.yaml). Repointed at
    # the canonical /audit route every other entity-detail link uses.
    #
    # F79 canonical /audit deep-link (screen-guide.md §6c), same shape as every
    # other entity-detail audit link (tenant, role, template, asset-class):
    # `query:` pre-applies the ItemLot filter on load, `return_context:` gives
    # /audit a Back to here. `audit` is a ReturnContextOnly archetype, so without
    # the second key the destination renders no Back at all — the defect
    # XP85-UX-015 now refuses, and the one the 2026-07 pass reported.
    #
    # The keys sit on the item itself because that is the shape the siblings use,
    # NOT because a nested `action:` block would drop them. It would not: the
    # parser reads `ParseScalarMap(nested ?? itemMapping, "query")` and
    # `GetScalar(nested, "return_context")`, so all three placements — inside
    # `action:`, sibling to it, or flat — emit the identical navigation. Measured
    # by holding one baseline and varying only the placement.
    - label: $t.lot_detail.btn_audit
      a11y:
        label: $t.lot_detail.btn_audit_label
      type: navigate
      route: /audit
      query:
        entityType: ItemLot
        entityId: $route.lotId
      return_context: origin_only
      icon: $icon.clipboard-text
    - label: $t.lot_detail.btn_scrap
      a11y:
        label: $t.lot_detail.btn_scrap_label
      action:
        type: navigate
        route: /materials/lots/:lotId/scrap
        params:
          lotId: $route.lotId
      icon: $icon.trash
      color: stop

# ── Summary card: 8 most important fields in a responsive Grid ───────────────
# Per patterns.md § Detail page composition § Summary card

lot-detail-info:
  type: Card
  spatial:
    padding: $spacing.lg
  children:
    - lot-detail-info-grid

lot-detail-info-grid:
  type: Grid
  spatial:
    columns: 4
    gap: $spacing.md
    responsive:
      sm:
        columns: 1
      md:
        columns: label-value
  children:
    - lot-info-material-label
    - lot-info-material-value
    - lot-info-initial-qty-label
    - lot-info-initial-qty-value
    - lot-info-current-qty-label
    - lot-info-current-qty-value
    - lot-info-operational-label
    - lot-info-operational-value
    - lot-info-receipt-label
    - lot-info-receipt-value
    - lot-info-expiry-label
    - lot-info-expiry-value
    - lot-info-source-label
    - lot-info-source-value
    - lot-info-supplier-label
    - lot-info-supplier-value
    - lot-info-quality-label
    - lot-info-quality-value

lot-info-material-label:
  type: Text
  content: $t.lot_detail.label_material
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

lot-info-material-value:
  type: Text
  bind:
    content: lot.itemDefinitionId
  tokens:
    font_size: $font.size.md

lot-info-initial-qty-label:
  type: Text
  content: $t.lot_detail.label_initial_qty
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

lot-info-initial-qty-value:
  type: Text
  bind:
    content: lot.initialQuantity.value
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono

lot-info-current-qty-label:
  type: Text
  content: $t.lot_detail.label_current_qty
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

lot-info-current-qty-value:
  type: Text
  bind:
    content: lot.currentQuantity.value
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono

lot-info-operational-label:
  type: Text
  content: $t.lot_detail.label_operational
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

lot-info-operational-value:
  type: StatusBadge
  bind:
    content: lot.operationalStatus
    variant:
      when: "lot.operationalStatus == 'ACTIVE'"
      value: go
      else: caution

lot-info-receipt-label:
  type: Text
  content: $t.lot_detail.label_receipt_date
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

lot-info-receipt-value:
  type: Text
  bind:
    content: lot.receiptDate
  format: datetime
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono

lot-info-expiry-label:
  type: Text
  content: $t.lot_detail.label_expiry_date
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

lot-info-expiry-value:
  type: Text
  bind:
    content: lot.expiryDate
  format: datetime
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono

lot-info-source-label:
  type: Text
  content: $t.lot_detail.label_source_type
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

lot-info-source-value:
  type: Text
  bind:
    content: lot.sourceType
  tokens:
    font_size: $font.size.md

lot-info-supplier-label:
  type: Text
  content: $t.lot_detail.label_supplier
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

lot-info-supplier-value:
  type: Text
  bind:
    content: lot.supplierId
  tokens:
    font_size: $font.size.md

lot-info-quality-label:
  type: Text
  content: $t.lot_detail.label_quality
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

lot-info-quality-value:
  type: StatusBadge
  bind:
    content: lot.qualityStatus
    variant:
      when: "lot.qualityStatus == 'RELEASED'"
      value: go
      else:
        when: "lot.qualityStatus == 'REJECTED'"
        value: stop
        else: caution

# ── Genealogy section ────────────────────────────────────────────────────────

lot-detail-genealogy:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.sm
  children:
    - genealogy-heading
    - genealogy-parents
    - genealogy-children

genealogy-heading:
  type: Text
  content: $t.lot_detail.heading_genealogy
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.lg
    font_family: $font.family.display
    color: $color_scheme.light.color.on-surface

genealogy-parents:
  type: List
  each: $data.genealogy.parents
  template: genealogy-link-item
  empty: genealogy-empty-parents
  a11y:
    label: $t.lot_detail.genealogy_parents_label

genealogy-link-item:
  type: Section
  spatial:
    display: flex
    align: center
    gap: $spacing.md
    padding: $spacing.sm
  tokens:
    border: $border.default
    radius: $radius.sm
  children:
    - genealogy-link-lot-id
    - genealogy-link-qty

genealogy-link-lot-id:
  type: Text
  bind:
    content: item.relatedLotId
  tokens:
    font_family: $font.family.mono
    font_size: $font.size.sm

genealogy-link-qty:
  type: Text
  bind:
    content: item.quantityUsed.value
  tokens:
    font_family: $font.family.mono
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

genealogy-empty-parents:
  type: EmptyState
  content: $t.common.empty_no_parents
  body: $t.common.empty_no_parents_body

genealogy-children:
  type: List
  each: $data.genealogy.children
  template: genealogy-link-item
  empty: genealogy-empty-children
  a11y:
    label: $t.lot_detail.genealogy_children_label

genealogy-empty-children:
  type: EmptyState
  content: $t.common.empty_no_children
  body: $t.common.empty_no_children_body
